// Visibility / dependency engine. Mirrors the sibling-first, walk-outward
// source-field resolution in
// app/javascript/components/DynamicFormBuilder/VisibilityEditor.jsx
// (`eligibleSourceKeys`) and the operator semantics documented on
// DynamicForms::Field. AND semantics across a field's rules; no OR in v1.

import type { DynamicFormField, VisibilityRule } from './types'

/**
 * One level of the field tree during resolution: the sibling field definitions
 * at that level paired with the value object holding their values. Ordered
 * root-first; the field under test lives at the last level. A repeatable-group
 * row is its own level (its `value` is the row object), so dependencies resolve
 * per-row first and then walk outward — which a flat (ancestors, rootValue)
 * pair could not express.
 */
export interface ResolutionLevel {
  fields: DynamicFormField[]
  value: Record<string, unknown> | undefined
}

const warnedOperators = new Set<string>()

function warnOnce(key: string, message: string): void {
  if (warnedOperators.has(key)) return
  warnedOperators.add(key)
  // eslint-disable-next-line no-console
  console.warn(`[dynamic-form-renderer] ${message}`)
}

/** Reset the warn-once cache. Test seam only. */
export function resetVisibilityWarnings(): void {
  warnedOperators.clear()
}

/**
 * Resolve a `source_field_key` to its definition and current value, searching
 * siblings at the field's own level first, then walking outward to the root.
 * The field under test is skipped at its own level. Returns undefined when no
 * level declares the key.
 */
export function resolveSourceField(
  key: string,
  field: DynamicFormField,
  levels: ResolutionLevel[],
): { field: DynamicFormField; value: unknown } | undefined {
  return resolveSourceLevel(key, field, levels)
}

/** As {@link resolveSourceField}, plus the level index — the source's own resolution stack. */
function resolveSourceLevel(
  key: string,
  field: DynamicFormField,
  levels: ResolutionLevel[],
): { field: DynamicFormField; value: unknown; index: number } | undefined {
  for (let i = levels.length - 1; i >= 0; i--) {
    const level = levels[i]
    const isOwnLevel = i === levels.length - 1
    for (const sibling of level.fields) {
      if (isOwnLevel && sibling === field) continue
      if (sibling.key === key) {
        return { field: sibling, value: level.value?.[key], index: i }
      }
    }
  }
  return undefined
}

/**
 * True when `field` should be shown given the current values. Fields with no
 * rules are always visible; otherwise every rule must pass (AND).
 */
export function isFieldVisible(field: DynamicFormField, levels: ResolutionLevel[]): boolean {
  return visibleWithin(field, levels, new Set())
}

function visibleWithin(
  field: DynamicFormField,
  levels: ResolutionLevel[],
  visiting: Set<DynamicFormField>,
): boolean {
  const rules = field.visibility
  if (!Array.isArray(rules) || rules.length === 0) return true
  return rules.every((rule) => evaluateRule(rule, field, levels, visiting))
}

/**
 * Mirrors the end of Alpaca's determineSingleDependencyValid: a rule only counts
 * when the field it reads is on screen. Narrower than Alpaca, which tests the
 * DOM and so also catches `options.hidden` — those are carriers, held off screen
 * precisely so other fields can read them, so only failing rules cascade here.
 */
function sourceOnScreen(
  source: { field: DynamicFormField; index: number },
  levels: ResolutionLevel[],
  visiting: Set<DynamicFormField>,
): boolean {
  // Self-referential definition: let the value comparison decide.
  if (visiting.has(source.field)) return true
  visiting.add(source.field)
  try {
    return visibleWithin(source.field, levels.slice(0, source.index + 1), visiting)
  } finally {
    visiting.delete(source.field)
  }
}

function evaluateRule(
  rule: VisibilityRule,
  field: DynamicFormField,
  levels: ResolutionLevel[],
  visiting: Set<DynamicFormField>,
): boolean {
  if (rule == null || typeof rule !== 'object') return true
  const resolved = resolveSourceLevel(rule.source_field_key, field, levels)
  if (resolved && !sourceOnScreen(resolved, levels, visiting)) return false
  const sourceValue = resolved?.value

  switch (rule.operator) {
    case 'present':
      return isPresent(sourceValue)
    case 'equals':
      return anyEquality(sourceValue, rule.value)
    case 'not_equals':
      return !anyEquality(sourceValue, rule.value)
    case 'in':
      return Array.isArray(rule.value) && anyEquality(sourceValue, rule.value)
    case 'not_in':
      return !(Array.isArray(rule.value) && anyEquality(sourceValue, rule.value))
    default:
      // Forward-compat: unknown operator leaves the field visible.
      warnOnce(
        `op:${String(rule.operator)}`,
        `unknown visibility operator ${JSON.stringify(rule.operator)} — treating field as visible`,
      )
      return true
  }
}

/** Mirrors Ruby's `present?`: false / nil / blank string / empty collection are absent. */
function isPresent(value: unknown): boolean {
  if (value == null || value === false) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value as object).length > 0
  return true
}

/**
 * Equality tolerant of JSON-type drift between option values and stored values
 * (e.g. number 1 vs string "1"). Objects/arrays compare by reference only.
 */
function looseEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a === 'object' || typeof b === 'object') return false
  return String(a) === String(b)
}

function members(value: unknown): unknown[] {
  if (Array.isArray(value)) return value
  if (value != null && typeof value === 'object') return Object.values(value as object)
  return [value]
}

/**
 * Mirrors Alpaca.anyEquality (alpaca-1.5.29.js:7684): a set intersection, so a
 * rule against an array-valued source passes when any selected value matches.
 * Two scalars reduce to the loose equality used before.
 */
function anyEquality(a: unknown, b: unknown): boolean {
  const bs = members(b)
  return members(a).some((x) => bs.some((y) => looseEqual(x, y)))
}
